import { Suspense, useEffect } from 'react';
import { lazyImport } from './utils/lazyImport';
import logger from './utils/logger';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BackgroundUploadProvider, useBackgroundUpload } from './contexts/BackgroundUploadContext';
import { StudentPlanningProvider } from './contexts/StudentPlanningContext';
import PWAProvider from './components/PWAProvider';
import { ModalManagerProvider } from './components/ui/modal/ModalManager';
import MainLayout from './components/MainLayout';
import LoadingSpinner from './components/LoadingSpinner';
import InviteRedirect from './components/InviteRedirect';
import AuthHashRedirect from './components/AuthHashRedirect';
import { trackPageview } from './lib/analytics';
import { hideBootSplash, isStudentBootScreen } from './lib/bootSplash';
import { restoreStatusBarDefault } from './lib/statusBarColor';
import { SESSION_EXPIRED_PATH, isSessionExpiring } from './lib/sessionExpiry';
import { getAdminView } from './lib/adminView';

// Indicateur d'envoi chargé seulement quand un envoi existe : il est monté
// globalement mais n'affiche rien sans upload — inutile de le payer au démarrage
// (budget du chunk principal, cf. .size-limit.json).
const BackgroundUploadIndicator = lazyImport(() => import('./components/BackgroundUploadIndicator'));

function BackgroundUploadIndicatorSlot() {
  const { activeUploads } = useBackgroundUpload();
  if (activeUploads.length === 0) return null;
  return (
    <Suspense fallback={null}>
      <BackgroundUploadIndicator />
    </Suspense>
  );
}

// Pastille « Mode admin » : seulement quand l'équipe est dans l'espace d'un coach.
const AdminViewBanner = lazyImport(() => import('./components/AdminViewBanner'));

function AdminViewBannerSlot() {
  const { user } = useAuth();
  if (!user || !getAdminView()) return null;
  return (
    <Suspense fallback={null}>
      <AdminViewBanner />
    </Suspense>
  );
}

// Lazy-loaded pages (code splitting); lazyImport recovers from stale chunk / HTML MIME errors after deploy
const LoginPage = lazyImport(() => import('./pages/LoginPage'));
const RegisterPage = lazyImport(() => import('./pages/RegisterPage'));
const StudentRegisterPage = lazyImport(() => import('./pages/StudentRegisterPage'));
const ForgotPasswordPage = lazyImport(() => import('./pages/ForgotPasswordPage'));
const ConfirmEmailPage = lazyImport(() => import('./pages/ConfirmEmailPage'));
const AuthCallback = lazyImport(() => import('./pages/AuthCallback'));
const CompleteProfilePage = lazyImport(() => import('./pages/CompleteProfilePage'));
const RegistrationSuccessPage = lazyImport(() => import('./pages/RegistrationSuccessPage'));
const OnboardingPage = lazyImport(() => import('./pages/OnboardingPage'));
const ResetPasswordPage = lazyImport(() => import('./pages/ResetPasswordPage'));
const CoachDashboard = lazyImport(() => import('./pages/CoachDashboard'));
const StudentDashboard = lazyImport(() => import('./pages/StudentDashboard'));
const StudentHistoryPage = lazyImport(() => import('./pages/StudentHistoryPage'));
const ExerciseManagement = lazyImport(() => import('./pages/ExerciseManagement'));
const TemplatesPage = lazyImport(() => import('./pages/TemplatesPage'));
const QuestionnairesPage = lazyImport(() => import('./pages/QuestionnairesPage'));
const StudentQuestionnairePage = lazyImport(() => import('./pages/StudentQuestionnairePage'));
const StudentBilansPage = lazyImport(() => import('./pages/StudentBilansPage'));
const ChatPage = lazyImport(() => import('./pages/ChatPage'));
const StudentChatPage = lazyImport(() => import('./pages/StudentChatPage'));
const VideoLibrary = lazyImport(() => import('./pages/VideoLibrary'));
const StudentVideoLibrary = lazyImport(() => import('./pages/StudentVideoLibrary'));
const LandingPage = lazyImport(() => import('./pages/LandingPage'));
const MentionsLegalesPage = lazyImport(() => import('./pages/MentionsLegalesPage'));
const CGUPage = lazyImport(() => import('./pages/CGUPage'));
const PolitiqueConfidentialitePage = lazyImport(() => import('./pages/PolitiqueConfidentialitePage'));
const FacturationPage = lazyImport(() => import('./pages/FacturationPage'));
const CoachRankings = lazyImport(() => import('./pages/CoachRankings'));
const CoachCompetitions = lazyImport(() => import('./pages/CoachCompetitions'));
const StudentRankings = lazyImport(() => import('./pages/StudentRankings'));
const StudentMatchSheetPage = lazyImport(() => import('./pages/StudentMatchSheetPage'));
const StudentSessionRecapPage = lazyImport(() => import('./pages/StudentSessionRecapPage'));
const SessionExpiredPage = lazyImport(() => import('./pages/SessionExpiredPage'));
const PublicCandidaturePage = lazyImport(() => import('./pages/PublicCandidaturePage'));
const AdminArea = lazyImport(() => import('./pages/admin/AdminArea'));
const AdminAccessLandingPage = lazyImport(() => import('./pages/AdminAccessLandingPage'));

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [], excludeLayout = false, allowPendingQuestionnaire = false }) => {
  try {
    const { user, loading } = useAuth();

    if (loading) {
      return <LoadingSpinner />;
    }

    if (!user) {
      // Session qui vient d'expirer : la page 401, pas /login (voir sessionExpiry).
      return <Navigate to={isSessionExpiring() ? SESSION_EXPIRED_PATH : '/login'} replace />;
    }

    // Google OAuth users without a phone must complete their profile first
    if (user.needsPhone) {
      return <Navigate to="/complete-profile" replace />;
    }

    // A new student answers their coach's onboarding questionnaire first.
    const pendingQuestionnaire = user.role === 'student' && user.pendingOnboardingQuestionnaire;
    if (pendingQuestionnaire && !allowPendingQuestionnaire) {
      return <Navigate to={`/student/questionnaire/${pendingQuestionnaire.id}`} replace />;
    }

    if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
      // Redirect to appropriate dashboard based on user role
      switch (user.role) {
        case 'admin':
        case 'coach':
          return <Navigate to="/coach/dashboard" replace />;
        case 'student':
          return <Navigate to="/student/dashboard" replace />;
        default:
          return <Navigate to="/coach/dashboard" replace />;
      }
    }

    // Skip MainLayout if excludeLayout is true (for full-screen mobile pages like StudentChatPage)
    if (excludeLayout) {
      return children;
    }

    return (
      <MainLayout>
        {children}
      </MainLayout>
    );
  } catch (error) {
    // If useAuth fails, it means AuthProvider is not available
    logger.error('AuthProvider not available:', error);
    return <LoadingSpinner />;
  }
};

// Chat Route Component - conditionally renders StudentChatPage or ChatPage based on user role
const ChatRouteWrapper = () => {
  const { user, loading } = useAuth();

  if (loading) {
    // Show loading within MainLayout for coaches, or full screen for students
    return user?.role === 'coach' ? (
      <MainLayout>
        <div className="h-full flex items-center justify-center">
          <div
            className="rounded-full border-2 border-transparent animate-spin"
            style={{
              borderTopColor: '#d4845a',
              borderRightColor: '#d4845a',
              width: '40px',
              height: '40px'
            }}
          />
        </div>
      </MainLayout>
    ) : <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to={isSessionExpiring() ? SESSION_EXPIRED_PATH : '/login'} replace />;
  }

  // Check if user has permission
  if (!['coach', 'student'].includes(user.role)) {
    return <Navigate to="/login" replace />;
  }

  // Render StudentChatPage for students (full-screen mobile, no MainLayout)
  if (user.role === 'student') {
    return <StudentChatPage />;
  }

  // Default to ChatPage for coaches (with MainLayout)
  return (
    <MainLayout>
      <ChatPage />
    </MainLayout>
  );
};

// Home Route — public landing; authenticated users go to their dashboard.
const HomeRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (user) {
    switch (user.role) {
      case 'admin':
      case 'coach':
        return <Navigate to="/coach/dashboard" replace />;
      case 'student':
        return <Navigate to="/student/dashboard" replace />;
      default:
        return <Navigate to="/coach/dashboard" replace />;
    }
  }
  return <LandingPage />;
};

// Fires a PostHog $pageview on every SPA route change. Renders nothing.
// Lives inside the Router (provided by BrowserRouter in main.jsx) so useLocation works.
const AnalyticsPageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
};

// Fades the index.html boot splash out once the app can show a real screen:
// the auth state is known AND the route's lazy chunk has loaded — it sits
// inside the <Suspense> boundary, so its effect only runs once that boundary
// has resolved. Outside it, the splash faded on auth alone and the Suspense
// spinner flashed before the page on a cold load. Student screens are left
// out: they hide the splash themselves once their data is in
// (useBootSplashReady), otherwise the logo gave way to the dashboard's own
// spinner. Renders nothing.
const BootSplashGate = () => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  useEffect(() => {
    if (loading) return;
    if (user?.role === 'student' && isStudentBootScreen(pathname)) return;
    hideBootSplash();
    // The launch script (index.html) gave the bar the splash's colour. A
    // coach screen never sets its own: give it the coach default. Everyone
    // else (visitor on the landing / auth pages, student off a boot screen)
    // gets a student-styled screen that owns the bar — and those pages are
    // lazy-loaded, so restoring here would flash the coach grey while their
    // chunk is still on its way.
    if (user?.role === 'coach') restoreStatusBarDefault();
  }, [loading, user?.role, pathname]);
  return null;
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <BackgroundUploadProvider>
      <StudentPlanningProvider>
        <PWAProvider>
          <ModalManagerProvider>
            <>
              <div className="App">
                <AnalyticsPageTracker />
                <AuthHashRedirect />
                <Suspense fallback={<LoadingSpinner />}>
                <BootSplashGate />
                <Routes>
                  {/* Public Routes - root is login; landing/waitlist at /waitlist */}
                  <Route path="/" element={<HomeRoute />} />
                  <Route path="/waitlist" element={<Navigate to="/" replace />} />
                  <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
                  <Route path="/cgu" element={<CGUPage />} />
                  <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/register/student" element={<StudentRegisterPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/invite" element={<InviteRedirect />} />
                  <Route path="/auth/confirm-email" element={<ConfirmEmailPage />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/registration/success" element={<RegistrationSuccessPage />} />
                  <Route path="/complete-profile" element={<CompleteProfilePage />} />
                  <Route path={SESSION_EXPIRED_PATH} element={<SessionExpiredPage />} />
                  {/* Candidature of a coach (link in their Instagram bio), no account */}
                  <Route path="/q/:handle" element={<PublicCandidaturePage />} />
                  <Route path="/q/:handle/:slug" element={<PublicCandidaturePage />} />
                  <Route path="/onboarding" element={
                    <ProtectedRoute allowedRoles={['student']} excludeLayout={true} allowPendingQuestionnaire>
                      <OnboardingPage />
                    </ProtectedRoute>
                  } />

                  {/* Coach Routes */}
                  <Route
                    path="/coach/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={['coach']}>
                        <CoachDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/templates"
                    element={
                      <ProtectedRoute allowedRoles={['coach']}>
                        <TemplatesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/questionnaires"
                    element={
                      <ProtectedRoute allowedRoles={['coach']}>
                        <QuestionnairesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/exercises"
                    element={
                      <ProtectedRoute allowedRoles={['coach']}>
                        <ExerciseManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/videotheque"
                    element={
                      <ProtectedRoute allowedRoles={['coach']}>
                        <VideoLibrary />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/rankings"
                    element={
                      <ProtectedRoute allowedRoles={['coach']}>
                        <CoachRankings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/competitions"
                    element={
                      <ProtectedRoute allowedRoles={['coach']}>
                        <CoachCompetitions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/competitions/:id"
                    element={
                      <ProtectedRoute allowedRoles={['coach']}>
                        <CoachCompetitions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/competitions/entries/:entryId"
                    element={
                      <ProtectedRoute allowedRoles={['coach']}>
                        <CoachCompetitions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/coach/facturation"
                    element={
                      <ProtectedRoute allowedRoles={['coach']}>
                        <FacturationPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Student Routes */}
                  <Route
                    path="/student/questionnaire/:responseId"
                    element={
                      <ProtectedRoute allowedRoles={['student']} excludeLayout allowPendingQuestionnaire>
                        <StudentQuestionnairePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/student/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={['student']}>
                        <StudentDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/student/bilans"
                    element={
                      <ProtectedRoute allowedRoles={['student']}>
                        <StudentBilansPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/student/history"
                    element={
                      <ProtectedRoute allowedRoles={['student']}>
                        <StudentHistoryPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/student/videos"
                    element={
                      <ProtectedRoute allowedRoles={['student']}>
                        <StudentVideoLibrary />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/student/rankings"
                    element={
                      <ProtectedRoute allowedRoles={['student']}>
                        <StudentRankings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/student/competitions/:entryId"
                    element={
                      <ProtectedRoute allowedRoles={['student']}>
                        <StudentMatchSheetPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/student/recap/:assignmentId"
                    element={
                      <ProtectedRoute allowedRoles={['student']}>
                        <StudentSessionRecapPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Équipe Kaiylo : pilotage et accès à l'espace d'un coach (API réservée à ADMIN_USER_IDS) */}
                  <Route
                    path="/admin/*"
                    element={
                      <ProtectedRoute allowedRoles={['coach']}>
                        <AdminArea />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/acces-admin" element={<AdminAccessLandingPage />} />

                  {/* Chat Route - Conditionally renders StudentChatPage for students, ChatPage for coaches */}
                  <Route
                    path="/chat"
                    element={
                      <ProtectedRoute allowedRoles={['coach', 'student']} excludeLayout={true}>
                        <ChatRouteWrapper />
                      </ProtectedRoute>
                    }
                  />

                  {/* Default redirect */}
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
                </Suspense>
              </div>
              {/* Monté globalement : les envois en arriere-plan (retours video
                  coach, videos de seance) restent visibles quelle que soit la
                  page, et survivent a la fermeture de la modale d'origine. */}
              <BackgroundUploadIndicatorSlot />
              <AdminViewBannerSlot />
            </>
          </ModalManagerProvider>
        </PWAProvider>
      </StudentPlanningProvider>
      </BackgroundUploadProvider>
    </AuthProvider>
  );
}

export default App;
